import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemoizedArrayData } from 'hooks/api/api';
import type { ApiResponse } from 'hooks/api/api';
import { AutomationsEmployeesService } from 'src/api/generated';
import type {
  EmployeeExecutionListQueryParams,
  TCEmployeeExecutionListResponse,
  TCEmployeeStepExecutionListResponse,
} from 'src/api/generated';

interface EmployeeExecutionsResponse extends ApiResponse {
  executions: TCEmployeeExecutionListResponse['items'];
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useEmployeeExecutions = (
  employeeId: string,
  params: EmployeeExecutionListQueryParams
): EmployeeExecutionsResponse => {
  const { version, limit = 20, from, to } = params;

  const fetchExecutions = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<TCEmployeeExecutionListResponse> => {
    return await AutomationsEmployeesService.listExecutions(
      employeeId,
      limit,
      pageParam,
      version,
      from,
      to
    );
  };

  const getNextPageParam = (lastPage: TCEmployeeExecutionListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['automations/employees/executions', employeeId, params],
    queryFn: fetchExecutions,
    getNextPageParam,
    initialPageParam: undefined,
  });

  const executions = useMemoizedArrayData(
    data?.pages.flatMap((page) => page.items) ?? []
  );

  return {
    loading,
    error,
    executions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

interface StepExecutionsResponse extends ApiResponse {
  stepExecutions: TCEmployeeStepExecutionListResponse;
}

export const useEmployeeStepExecutions = (
  executionId: string
): StepExecutionsResponse => {
  const {
    data: stepExecutions,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['automations/employees/executions/steps', executionId],
    queryFn: () => AutomationsEmployeesService.listStepExecutions(executionId),
  });

  return {
    loading,
    error,
    stepExecutions: stepExecutions ?? [],
    refetch,
  };
};
